import { DoorDTO, LocationDTO } from "src/app/api/api-types";
import { getEnumMember } from "src/utils";
import { LocationStatus, LocationStatusEnum } from "./location-status.model";

export enum TrailerLocationCodesEnum {
  Yard = "Yard",
  Door = "Door",
  ParkingSpot = "Parking Spot",
  Center = "Center",
  Gate = "Gate",
}

export class TrailerLocation {
  public constructor(
    args: Omit<
      ClassProperties<TrailerLocation>,
      "isDoor" | "isYard" | "isOutOfService"
    >,
  ) {
    this.code = args.code;
    this.id = args.id;
    this.isOccupied = args.isOccupied;
    this.mrDoorName = args.mrDoorName;
    this.mrDoorId = args.mrDoorId;
    this.name = args.name;
    this.status = args.status;

    // Computed properties
    this.isDoor = this.code === TrailerLocationCodesEnum.Door;
    this.isYard = this.code !== TrailerLocationCodesEnum.Door;

    this.isOutOfService = this.status
      ? this.status.value === LocationStatusEnum.OutOfService
      : false;
  }

  public readonly code: TrailerLocationCodesEnum;
  public readonly id: number;
  public readonly isOccupied: boolean;
  public readonly mrDoorName: string;
  public readonly mrDoorId: number | null;
  public readonly name: string;
  public readonly status: LocationStatus | null;

  // Computed Properties
  public readonly isDoor: boolean;
  public readonly isYard: boolean;
  public readonly isOutOfService: boolean;

  public static deserialize(data: DoorDTO | LocationDTO): TrailerLocation {
    return new TrailerLocation({
      code: getEnumMember(TrailerLocationCodesEnum, data.typeCode),
      id: data.id,
      isOccupied:
        "isOccupied" in data
          ? data.isOccupied
          : data.status?.value === LocationStatusEnum.Occupied,
      mrDoorName: data.mrName,
      mrDoorId: data.mrLocationId,
      name: data.name,
      status: data.status && LocationStatus.deserialize(data.status),
    });
  }

  public static deserializeList(
    data: DoorDTO[] | LocationDTO[],
  ): readonly TrailerLocation[] {
    return data.map((x) => TrailerLocation.deserialize(x)) || [];
  }
}

export function getDoorLocations(
  locations: readonly TrailerLocation[],
): TrailerLocation[] {
  return locations.filter((location) => location.isDoor);
}

export function getYardLocations(
  locations: readonly TrailerLocation[],
): TrailerLocation[] {
  return locations.filter((location) => location.isYard);
}

export function getAllLocationsWithTheirOccupancyStatus(
  locations: readonly TrailerLocation[],
  doorLocations: readonly TrailerLocation[],
): TrailerLocation[] {
  return locations.map((location) => {
    if (location.isDoor) {
      // If location is door then we get its information from the doors list to ensure that
      // its property `isOccupied` is up-to-date.
      const doorLocation = doorLocations.find(
        (doorLocation) => location.id === doorLocation.id,
      );

      if (!doorLocation) {
        throw new Error(
          `Door location with ID ${location.id} is not found in the door list.`,
        );
      }

      return doorLocation;
    }
    return location;
  });
}
